import React from 'react';
import { Location } from 'history';
import { whereAmI } from 'helpers/errorHelpers';

// Components
import ErrorBar from './ErrorBar';
import { CurrentUserType } from 'sharedTypes/user/userTypes';

// Context
interface ErrorWrapperContext {
  errors: Error[];
  addError?: (error: Error, retryCb: () => void, rawError?: Error) => void;
  onRetry?: () => void;
  clearErrors?: () => void;
}
export const ErrorContext = React.createContext<ErrorWrapperContext>({ errors: [] });

export interface Props {
  children: React.ReactNode;
  className?: string;
  location?: Location;
  user?: CurrentUserType;
}

const defaultWrapperState: ErrorWrapperContext = {
  errors: [],
  onRetry: () => location.reload(),
  clearErrors: () => {
    //no op default
  }
};

export class ErrorWrapper extends React.Component<Props, ErrorWrapperContext> {
  constructor(props: Props) {
    super(props);
    this.state = { ...defaultWrapperState, addError: this.addError, clearErrors: this.clearErrors }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.location && prevProps.location) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.clearErrors();
      }
    }
  }

  addError = (error: Error, retryCb: () => void, rawError?: Error) => {
    /* istanbul ignore next */
    if (window.newrelic) {
      const { user, location } = this.props;
      const newRelic = window.newrelic;
      newRelic.noticeError(
        error,
        {
          type: 'ErrorBoundaryCaughtError',
          errorDetails: rawError || error,
          env: whereAmI(),
          userId: user?.UserId || user?.OktaId,
          location: location?.pathname,
          login: user?.EmailAddress
        }
      );
    }

    this.setState(state => {
      return {
        errors: state.errors.concat(error),
        onRetry: () => {
          this.setState({ ...this.state, errors: [] }, () => {
            if (retryCb) {
              retryCb();
            }
          });
        }
      };
    });
  };

  clearErrors = () => {
    this.setState({ ...this.state, errors: [] });
  };

  render() {
    const { children, className } = this.props;
    const { errors, onRetry } = this.state;
    return (
      <ErrorContext.Provider value={this.state}>
        <div className={className}>
          {errors.map((error) => {
            return <ErrorBar key={`error-${error.message}`} errorMessage={`${error.message},`} onRetry={onRetry} />
          })}
          {children}
        </div>
      </ErrorContext.Provider>
    );
  }
}
